import { useCallback, useEffect, useState } from 'react'
import { compose } from 'redux'

import { getAllAncestorsForItem } from '../helpers/getAllAncestorsForItem'
import { getAllDescendantsForItem } from '../helpers/getAllDescendantsForItem'
import { getTreeItemState } from '../helpers/getTreeItemState'
import {
  IUseSelectedValuesParameters,
  TUseSelectedValuesResult,
} from '../models/iUseSelectedValues'
import { ITreeStructure } from '../models/treeStructure'

export const useSelectedValues = ({
  values,
  flatData,
  typeOfSelectedNodes,
}: IUseSelectedValuesParameters): TUseSelectedValuesResult => {
  /** -------------------- Variables ----------------------- */
  const [selectedItems, setSelectedItems] = useState<ITreeStructure[]>([])

  /** -------------------- Methods ----------------------- */
  const getUpdatedSelectedItems = useCallback(() => {
    const initialSelectedValues = flatData.filter((it) =>
      values.some((selectedId) => selectedId === it.itemId)
    )

    return initialSelectedValues
      .reduce<ITreeStructure[]>(
        (acc, item) => [...getAllAncestorsForItem(item), ...getAllDescendantsForItem(item), ...acc],
        []
      )
      .map<ITreeStructure>((it, i, origin) => ({
        ...it,
        checkedState: getTreeItemState(it.itemId, flatData, origin),
      }))
  }, [values, flatData])

  const getDisplayedItemsByLeaves = useCallback(() => {
    const initialSelectedValues = flatData.filter((it) =>
      values.some((selectedId) => selectedId === it.itemId)
    )

    return initialSelectedValues
      .reduce<ITreeStructure[]>(
        (acc, item) => [...getAllAncestorsForItem(item), ...getAllDescendantsForItem(item), ...acc],
        []
      )
      .map<ITreeStructure>((it, i, origin) => ({
        ...it,
        checkedState: getTreeItemState(it.itemId, flatData, origin),
      }))
      .reduce((unique, current) => {
        return unique.some((item) => item.itemId === current.itemId) ? unique : [...unique, current]
      }, [] as ITreeStructure[])
  }, [values, flatData])

  /** -------------------- Effects ----------------------- */
  useEffect(() => {
    if (typeOfSelectedNodes === 'onlyLeavesItems') {
      compose(setSelectedItems, getDisplayedItemsByLeaves)()
      return
    }

    compose(setSelectedItems, getUpdatedSelectedItems)()
  }, [getUpdatedSelectedItems, getDisplayedItemsByLeaves, typeOfSelectedNodes])

  return [selectedItems, setSelectedItems]
}
